import React, { useEffect } from 'react';
import {
  QueryContext,
  arrayOf,
  bool,
  extend,
  params,
  shape,
  string,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { StoreFinderRedirector } from '@thd-nucleus/app-render';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import {
  PromoPod,
  RentalsContent,
  RentalServicesInformation,
  RentalsFAQ
} from '@thd-olt-component-react/emt-generated-content';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { RentalsMetadata } from '@thd-olt-component-react/metadata';
import { PopularCategories } from '@thd-olt-component-react/related-search';
import { useLocation, useParams } from '@thd-olt-component-react/router';
import {
  StoreHero,
  StoreHeroDetails,
  StoreHeroHeader,
  StoreHeroServices
} from '@thd-olt-component-react/store-hero';

import { NearbyStores } from '../../shared-components/NearbyStores';
import { StoreFinderBreadcrumbs } from '../../shared-components/StoreFinderBreadcrumbs';

export const RentalServices = () => {
  declareContexts([QueryContext], { freeze: true });

  const {
    storeNumber, storeZip, state, name, city
  } = useParams();
  const path = useLocation();

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('store-finder.rental-page-load', {
      storeNumber,
      storeZip,
      state,
      name,
      city
    });
  }, [storeNumber]);

  const phoneNumbersToInclude = {
    store: false,
    pro: false,
    tool: true
  };

  const options = {
    variables: {
      storeSearchInput: storeNumber,
      pagesize: '1'
    }
  };

  const { data } = useDataModel('storeSearch', options);
  const storeServices = data?.storeSearch?.stores?.[0]?.services;
  let hasRentalServicesFromStoreSearch = false;
  if (storeServices) {
    const {
      toolRental, largeEquipment, loadNGo, penske, hdMoving
    } = storeServices;
    hasRentalServicesFromStoreSearch = toolRental || largeEquipment || loadNGo || penske || hdMoving;
  }

  const servicesWidth = hasRentalServicesFromStoreSearch ? '8' : '12';
  const serviceClassNames = `sui-flex sui-col-span-12 lg:sui-col-span-${servicesWidth}`;

  return (
    <>
      <ErrorBoundary name="rental-services-redirector">
        <StoreFinderRedirector />
      </ErrorBoundary>

      <ErrorBoundary name="rental-services-metadata">
        <RentalsMetadata storeId={storeNumber} />
      </ErrorBoundary>

      <div className="sui-max-w-screen-2xl sui-m-auto">
        <div className="sui-flex sui-flex-col sui-px-2 2xl:sui-px-0">
          <ErrorBoundary name="rental-services-breadcrumbs">
            <StoreFinderBreadcrumbs
              includeDirectory
              pageName="Tool & Truck Rentals"
            />
          </ErrorBoundary>

          <ErrorBoundary name="rental-services-header">
            <StoreHero
              storeId={storeNumber}
            >
              <StoreHeroHeader
                hideActionButton
                hideRating
                label="Rental Center at "
              />
              <section className="sui-grid sui-grid-cols-12 sui-gap-4">
                <div className={serviceClassNames}>
                  <StoreHeroServices type="rental" />
                </div>
                {hasRentalServicesFromStoreSearch && (
                  <div className="sui-col-span-12 lg:sui-col-span-4">
                    <StoreHeroDetails
                      phoneNumbers={phoneNumbersToInclude}
                      showStoreDetailsButton
                      title="Rental Center"
                      hoursType="rental"
                    />
                  </div>
                )}
              </section>
            </StoreHero>
          </ErrorBoundary>

          <section className="sui-flex sui-flex-col sui-gap-y-8 sui-mt-8">
            <ErrorBoundary name="rental-services-promo-pod">
              <PromoPod contentId={storeNumber} pageName="rentals" />
            </ErrorBoundary>

            <ErrorBoundary name="rental-services-listings">
              <RentalServicesInformation contentId={storeNumber} />
            </ErrorBoundary>

            <ErrorBoundary name="emt-rental-services-content">
              <RentalsContent storeId={storeNumber} />
            </ErrorBoundary>

            <ErrorBoundary name="emt-rental-services-faq">
              <RentalsFAQ storeId={storeNumber} />
            </ErrorBoundary>

            <ErrorBoundary name="rentals-services-nearby-stores">
              <Hydrator
                id="rental-services-nearby-stores-map"
                scrollBuffer={800}
              >
                <NearbyStores
                  omitStoreNumber={storeNumber}
                  pageType="rentals"
                  storeNumber={storeNumber}
                  title="Nearby Rental Center"
                />
              </Hydrator>
            </ErrorBoundary>

            <ErrorBoundary name="rental-services-popular-categories">
              <PopularCategories
                contentId={storeNumber}
                pageType="rentals"
              />
            </ErrorBoundary>

            <ErrorBoundary name="rental-services-explore-more">
              <EmtLinks path={path.pathname} />
            </ErrorBoundary>
          </section>
        </div>
      </div>
    </>
  );
};

RentalServices.displayName = 'RentalServices';
RentalServices.propTypes = {};

RentalServices.defaultProps = {
  name: '',
};

RentalServices.dataModel = extend(
  {
    storeSearch: params({ pagesize: string().isRequired(), storeSearchInput: string().isRequired() }).shape({
      stores: arrayOf(shape({
        services: shape({
          hdMoving: bool(),
          largeEquipment: bool(),
          loadNGo: bool(),
          penske: bool(),
          toolRental: bool(),
        })
      }))
    })
  },
  Breadcrumbs,
  EmtLinks,
  NearbyStores,
  PopularCategories,
  PromoPod,
  RentalsContent,
  RentalsFAQ,
  RentalsMetadata,
  RentalServicesInformation,
  StoreFinderRedirector,
  StoreHero,
);